import { useEffect, useState } from "react";
import './user-drawer.less'
import { Breadcrumb, Button, Input, SideHeader } from "bimui";
import { Table, message } from "antd";
import classNames from 'classnames';
import { useParams } from "react-router-dom";
import { ReactComponent as BoxSelectedSvg } from './icons/box-select.svg';
import { ReactComponent as BoxSvg } from './icons/box.svg';
import { User } from "@/declare/interface/project/user";
import { addProjectUsersBatch, deleteProjectUsersBatch, findProjectUsers } from "@/declare/api/project/project-user";
import { ReactComponent as SearchSvg } from 'bimui/dist/c1/icons/search.svg';
import { ReactComponent as FilterSvg } from './icons/filter.svg';
import { ReactComponent as NewSvg } from './icons/new.svg';
import UserModal from "./user-model";
// 定义组件接收的属性接口
interface Iprops {
    users: User[]; // 系统库用户列表
    projectUsers: User[]; // 项目库用户列表
    onCancel?: () => void; // 取消操作的回调
    onSave?: (selectedItems: User[]) => void; // 保存操作的回调，传递 selectedItems
    onClose?: () => void; // 抽屉关闭的回调
}

/**
 * 用户侧边栏添加组件
 * 该组件用于展示和选择系统用户库中用户的信息。
 * 
 * @param {Iprops} props
 * @returns {JSX.Element} 渲染的组件
 */
export default function UserDrawer(props: Iprops) {
    const breadcrumbItems = [
        { label: '用户', onClick: () => { props.onCancel(); endAdd(); } },
        { label: '用户库' },
    ];
    /**
     * 从 URL 中提取项目 UUID 参数
     */
    const { projectUuid } = useParams();

    /**
     * 显示系统库的用户列表数据
     */
    const [user, setUser] = useState<User[]>([]);
    /**
     * 名称搜索后的用户列表数据
     */
    const [filtereduser, setFilteredUser] = useState<User[]>([]);
    /**
     * 当前选中的系统库用户列表
     */
    const [selectedItems, setSelectedItems] = useState<User[]>([]);
    /**
     * 之前选中的用户列表
     */
    const [oldSelectedItems, setOldSelectedItems] = useState<User[]>([]);
    /**
     * 本次选中的用户列表
     */
    const [currentSelectedItems, setCurrentSelectedItems] = useState<User[]>([]);

    /**
     * 控制用户信息弹窗的显示状态
     */
    const [showModal, setShowModal] = useState<boolean>(false);

    const [jobFilters, setJobFilters] = useState<{ text: string; value: string }[]>([]); // 职位筛选列表
    const [majorFilters, setMajorFilters] = useState<{ text: string; value: string }[]>([]); // 专业筛选列表
    /**
     * 筛选输入的名称
     */
    const [sectionName, setSectionName] = useState<string>('');


    /**
     * 组件初始化时加载数据
     */
    useEffect(() => {
        // 初始化待添加进项目的系统库用户列表
        const allUsers = props.users || [];
        const projectUsers = props.projectUsers || [];
        const waitingUsers = allUsers.filter((user) =>
            projectUsers.some((projectUser) => projectUser.uuid === user.uuid)
        );

        // 生成职位、专业筛选列表，确保类型为 string
        const uniqueJobs = Array.from(new Set(allUsers.map((item) => item.job).filter(Boolean))) as string[];
        const uniqueMajors = Array.from(new Set(allUsers.map((item) => item.major).filter(Boolean))) as string[];

        setJobFilters(uniqueJobs.map((job) => ({ text: job, value: job })));
        setMajorFilters(uniqueMajors.map((major) => ({ text: major, value: major })));

        setUser(allUsers); // 设置所有系统库用户
        setFilteredUser(allUsers); // 初始化时，展示所有用户
        setSelectedItems([...waitingUsers]); // 初始化已选用户
        setOldSelectedItems([...waitingUsers]); // 保存初始项目用户列表
    }, [props.users, props.projectUsers])


    /**
     * 确认选择并执行关联用户到项目库的操作
     */
    const handleSave = () => {
        if (!selectedItems.length) {
            message.warning("请选择至少一个用户！");
            return;
        }

        const toAdd = currentSelectedItems.map((item) => item.uuid); // 本次新增用户的 UUID 列表
        const toRemove = oldSelectedItems
            .filter((oldItem) => !selectedItems.some((item) => item.uuid === oldItem.uuid))
            .map((item) => item.uuid); // 本次移除用户的 UUID 列表

        const addPromise = toAdd.length ? addProjectUsersBatch(projectUuid, toAdd) : Promise.resolve();
        const removePromise = toRemove.length ? deleteProjectUsersBatch(projectUuid, toRemove) : Promise.resolve();

        Promise.all([addPromise, removePromise])
            .then(() => {
                findProjectUsers(projectUuid)
                    .then((res) => {
                        props.onSave?.(res.data); // 将最新的用户数据传递给父组件
                        endAdd();
                    })
                    .catch(() => {
                        message.error("获取最新的用户数据失败！");
                    });
            })
            .catch(() => {
                message.error("更新用户信息失败，请稍后重试！");
            });
    };

    /**
     * 全选/取消全选
     * 用于切换选中状态（如果已全选，则取消全选；否则全选）
     */
    const onSelectAllCick = () => {
        if (selectedItems.length === user.length) {
            // 当前已全选 -> 取消全选
            setSelectedItems([]);
            setCurrentSelectedItems([]);
        } else {
            // 当前未全选 -> 全选
            setSelectedItems([...user]);

            const newlySelected = user.filter(
                (u) => !oldSelectedItems.some((old) => old.uuid === u.uuid)
            );
            setCurrentSelectedItems(newlySelected);
        }
    };

    /**
     * 选择用户
     * @param record 用户数据
     */
    const onBoxClick = (record: User) => {
        const isSelected = selectedItems.some((item) => item.uuid === record.uuid);

        if (isSelected) {
            // 用户已选中 -> 取消选中
            setSelectedItems(selectedItems.filter((item) => item.uuid !== record.uuid));

            if (currentSelectedItems.some((item) => item.uuid === record.uuid)) {
                setCurrentSelectedItems(currentSelectedItems.filter((item) => item.uuid !== record.uuid));
            }
        } else {
            // 用户未选中 -> 选中
            setSelectedItems([...selectedItems, record]);

            if (!oldSelectedItems.some((item) => item.uuid === record.uuid)) {
                setCurrentSelectedItems([...currentSelectedItems, record]);
            }
        }
    };


    /**
     * 更新搜索框中的用户名称的状态、根据输入内容筛选选择列表。
     * @param e 输入事件。
     */
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim(); // 获取输入值并去掉两端空格
        setSectionName(value); // 更新输入框状态
        // 过滤符合搜索条件的用户，并更新 `filtereduser`
        const filtered = user.filter(u => u.name.includes(value));
        setFilteredUser(filtered);
    };

    /**
     * 成功新建用户的回调函数
     * @param newUser 
     */
    const handleNewUser = (newUser: User) => {
        // 更新 user 列表
        const updatedUserList = [...user, newUser];
        setUser(updatedUserList);
    
        // 更新 selectedItems
        const updatedSelectedItems = [...selectedItems, newUser];
        setSelectedItems(updatedSelectedItems);
    
        // 如果新建用户不在 oldSelectedItems 中，添加到 currentSelectedItems
        if (!oldSelectedItems.some((item) => item.uuid === newUser.uuid)) {
            const updatedCurrentSelectedItems = [...currentSelectedItems, newUser];
            setCurrentSelectedItems(updatedCurrentSelectedItems);
        }
    
        // 比对搜索框内容，更新 filtereduser
        if (
            !sectionName || // 如果搜索框为空
            newUser.name.includes(sectionName) // 或新用户的名称匹配搜索框内容
        ) {
            const updatedFilteredUser = [...filtereduser, newUser];
            setFilteredUser(updatedFilteredUser);
        }
    };
    

    /**
     * 结束添加组件
     */
    const endAdd = () => {
        setFilteredUser(null);
        setSelectedItems(null);
        setOldSelectedItems(null);
        setCurrentSelectedItems(null);
    };

    return (
        <div className="user-drawer" >
            <SideHeader>
                <div className='user-side-header'>
                    <Breadcrumb items={breadcrumbItems} />
                    <NewSvg className='add-icon' onClick={() => { setShowModal(true); }} />
                </div>
            </SideHeader>
            <div className="user-system-find" >
                <div className="input-with-icon">
                    <Input
                        value={sectionName}
                        onChange={handleNameChange}
                    />
                    <span className="input-icon">
                        <SearchSvg />
                    </span>
                </div>
            </div>
            <div className="user-system-table-area" >
                <Table
                    rowKey="uuid" // 数据唯一标识
                    pagination={false} // 禁用分页
                    dataSource={filtereduser || []} // 数据源
                    bordered={false} // 无边框
                    className='tn-common-table' // 自定义表格样式
                    rowClassName={(row) =>
                        classNames({
                            "common-table-row": true,
                            // "user-add-table-row-active": currentSelectedItems.some((item) => item.uuid === row.uuid),
                        })
                    }
                    sticky // 固定表头
                    onRow={(record) => ({
                        onClick: () => {
                            onBoxClick(record); // 点击整行时调用点击事件处理函数
                        },
                    })}
                >
                    <Table.Column
                        dataIndex="name"  // 保留原有的数据源
                        title={(<div className='left-head-cell' style={{ display: 'flex', gap: '4px' }}>
                            {/* 全选按钮 */}
                            {(selectedItems?.length !== user?.length || user?.length === 0) && (
                                <BoxSvg
                                    style={{ cursor: 'pointer' }}
                                    onClick={(e) => {
                                        e.stopPropagation(); // 阻止事件冒泡
                                        onSelectAllCick();
                                    }}
                                />
                            )}
                            {/* 取消全选按钮 */}
                            {selectedItems?.length === user?.length && user?.length !== 0 && (
                                <BoxSelectedSvg
                                    style={{ cursor: 'pointer' }}
                                    onClick={(e) => {
                                        e.stopPropagation(); // 阻止事件冒泡
                                        onSelectAllCick();
                                    }}
                                />
                            )}
                            姓名
                        </div>)}
                        width={`${130 / 340 * 100}%`}  // 设置列宽
                        ellipsis  // 启用文字省略
                        render={(value, record) => (

                            <div
                                style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                                onClick={() => onBoxClick(record)}  // 保持点击选择行的功能
                            >
                                {/* 保持选择框的显示 */}
                                {!selectedItems?.some((item) => item.id === record.id) && (<BoxSvg style={{ cursor: 'pointer' }} />)}
                                {selectedItems?.some((item) => item.id === record.id) && (<BoxSelectedSvg style={{ cursor: 'pointer' }} />)}
                                {/* 使用 index 来展示序号 */}
                                <div className={classNames({ "text-active": currentSelectedItems.some((item) => item.uuid === record.uuid) })}
                                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right', marginLeft: '4px', }}                                >
                                    {value}
                                </div>
                            </div>
                        )}
                        sorter={(a: User, b: User) => a.name?.localeCompare(b.name || '')} // 排序规则
                        showSorterTooltip={false} // 不显示排序图标
                    />

                    <Table.Column
                        dataIndex="job"
                        title="职位"
                        width={`${105 / 340 * 100}%`}
                        filters={jobFilters} // 筛选器列表
                        onFilter={(value, record) => record.job === value} // 筛选逻辑
                        ellipsis
                        filterIcon={() => <FilterSvg style={{ cursor: 'pointer' }} />} // 自定义筛选图标
                        sorter={(a: User, b: User) => a.job?.localeCompare(b.job || '')} // 排序规则
                        showSorterTooltip={false} // 不显示排序图标
                        render={(value, record) => {
                            const isActive = currentSelectedItems.some((item) => item.uuid === record.uuid);
                            return (
                                <span className={classNames({ "text-active": isActive })}>
                                    {value}
                                </span>
                            );
                        }}
                    />
                    <Table.Column
                        dataIndex="major"
                        title="专业"
                        width={`${105 / 340 * 100}%`}
                        filters={majorFilters} // 添加专业筛选器
                        onFilter={(value, record) => record.major === value} // 筛选逻辑
                        ellipsis
                        filterIcon={() => <FilterSvg style={{ cursor: 'pointer' }} />} // 自定义筛选图标
                        sorter={(a: User, b: User) => a.major?.localeCompare(b.major || '')} // 排序规则
                        showSorterTooltip={false} // 不显示排序图标
                        render={(value, record) => {
                            const isActive = currentSelectedItems.some((item) => item.uuid === record.uuid);
                            return (
                                <span className={classNames({ "text-active": isActive })}>
                                    {value}
                                </span>
                            );
                        }}
                    />
                </Table>
            </div>

            {
                showModal && <UserModal
                    showModal={showModal}
                    defaultUser={null}
                    onCancel={() => { setShowModal(false); }}
                    onNew={(newUser) => { handleNewUser(newUser);setShowModal(false); }}
                    onClose={() => { setShowModal(false); }}
                />
            }

            <div className='modal-footer'>
                <Button primary onClick={handleSave} >确定</Button>
                <Button danger onClick={() => { props.onCancel(); endAdd(); }}>取消</Button>
            </div>
        </div>
    )
}

